import { processPresetsPolyfill } from './utils'
import type { PresetPolyfillData } from './types'
import type { ILogger } from '@wix/thunderbolt-symbols'

/**
 * window resize observer for builder components polyfill
 */
export class BuilderComponentResizeObserver {
	private getPresetsPolyfillData: () => Record<string, PresetPolyfillData>
	private logger: ILogger
	private windowResizeHandler: (() => void) | null = null
	private isListening = false

	constructor(getPresetsPolyfillData: () => Record<string, PresetPolyfillData>, logger: ILogger) {
		this.getPresetsPolyfillData = getPresetsPolyfillData
		this.logger = logger
		this.windowResizeHandler = () => {
			let interactionStarted = false
			let componentsCount = 0

			try {
				const processedPolyfillData = this.getPresetsPolyfillData()
				componentsCount = Object.keys(processedPolyfillData).length

				this.logger.interactionStarted('builder-component-resize-polyfill', {
					customParams: { componentsCount },
				})
				interactionStarted = true

				processPresetsPolyfill(processedPolyfillData)
			} catch (error) {
				this.logger.captureError(error as Error, {
					tags: { feature: 'builder-component', method: 'windowResizeHandler' },
				})
			} finally {
				if (interactionStarted) {
					this.logger.interactionEnded('builder-component-resize-polyfill', {
						customParams: { componentsCount },
					})
				}
			}
		}
	}

	/**
	 * Start listening to window resize events
	 */
	startListening(): void {
		if (!this.isListening && this.windowResizeHandler) {
			window.addEventListener('resize', this.windowResizeHandler)
			this.isListening = true
		}
	}

	/**
	 * Stop listening and clean up all resources
	 */
	dispose(): void {
		if (this.isListening && this.windowResizeHandler) {
			window.removeEventListener('resize', this.windowResizeHandler)
			this.isListening = false
		}
		this.windowResizeHandler = null
	}
}

/**
 * Set up and start window resize observation for builder components
 * @param getPresetsPolyfillData - Function that returns fresh polyfill data on-demand
 * @param logger - Logger instance for performance tracking and error reporting
 * @returns The resize observer instance for cleanup
 */
export const builderComponentPolyfillResizeObserver = (
	getPresetsPolyfillData: () => Record<string, PresetPolyfillData>,
	logger: ILogger
): BuilderComponentResizeObserver => {
	const resizeObserver = new BuilderComponentResizeObserver(getPresetsPolyfillData, logger)
	resizeObserver.startListening()
	return resizeObserver
}
